/**
 * Global Presenter context — keeps a screen-share session alive
 * across route changes, with a minimize pill so the user can
 * navigate the rest of the app without losing the share.
 *
 * Mounted ONCE near the top of the React tree (in ``App.js`` inside
 * ``<BrowserRouter>``) so the underlying LiveKit ``Room`` is owned
 * by a component that doesn't unmount when the user leaves
 * ``/conversations-v2``.
 *
 * Usage:
 *   const { startPresenting, stopPresenting, current, minimized,
 *           setMinimized } = useScreenSharePresenter();
 *
 *   await startPresenting(conversationId);  // ← capture + start
 *
 * The provider renders the live modal (full-screen) when expanded
 * and a small pill in the bottom-right corner when minimized. The
 * share auto-ends ONLY on:
 *   1. ``stopPresenting()`` (explicit user click).
 *   2. The browser's native "Stop sharing" chrome (track ``ended``).
 *   3. Browser tab close (handled by ``beforeunload`` to keep the
 *      timeline event tidy — best-effort, browsers don't guarantee
 *      synchronous cleanup).
 *   4. Page refresh — same as tab close.
 *
 * Navigation between routes does NOT end the share (D9 §4 — Presenter
 * is "currently engaged" for the entire Active duration).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState,
} from "react";
import {
  Monitor, X as XIcon, ArrowsIn, ArrowsOut,
  Microphone, MicrophoneSlash, SpeakerHigh,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  startShare, endShare, captureScreen, openSignalingWs,
} from "@/lib/screen-share/client";
import { presenterConnect } from "@/lib/screen-share/livekitTransport";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const Ctx = createContext(null);

export function useScreenSharePresenter() {
  const v = useContext(Ctx);
  if (!v) {
    // Provider not mounted — return no-op stubs so callers can
    // gracefully degrade in unit tests / storybook.
    return {
      startPresenting: async () => {},
      stopPresenting: async () => {},
      current: null,
      minimized: false,
      setMinimized: () => {},
    };
  }
  return v;
}

export function PresenterProvider({ children }) {
  const t = useT();
  /** Tuple ``{share_id, conversation_id, token, peer_id, stream}`` or null. */
  const [current, setCurrent] = useState(null);
  const [minimized, setMinimized] = useState(false);
  // ``connecting | active | relaying | relay_unavailable | disconnected``
  const [phase, setPhase] = useState("connecting");
  const [relayActive, setRelayActive] = useState(false);
  const wsRef = useRef(null);
  const sfuRef = useRef(null);

  /**
   * Begin presenting in ``conversationId``. Captures the screen,
   * allocates a Share via the gateway, opens the signaling WS so
   * the share flips Active, and connects to LiveKit. Returns the
   * server response on success, throws on cancel / error.
   */
  const startPresenting = useCallback(async (conversationId) => {
    if (current) {
      toast.error("You're already presenting in another conversation");
      return null;
    }
    let stream;
    try {
      stream = await captureScreen();
    } catch (e) {
      // Cancel is the most common case — silent.
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        toast.error(e?.message || "Could not start screen capture");
      }
      throw e;
    }
    let started;
    try {
      started = await startShare(conversationId);
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      const machine = (() => {
        try { return JSON.parse(e?.response?.data?.detail || "{}").error; }
        catch { return ""; }
      })();
      if (machine === "share_cap_reached") {
        toast.error("Another screen share is already in progress");
      } else if (machine === "presenter_busy") {
        toast.error("You're already presenting in another conversation");
      } else if (machine === "level_too_low") {
        toast.error("You don't have permission to start a screen share");
      } else {
        toast.error(e?.response?.data?.detail || "Could not start screen share");
      }
      throw e;
    }
    setCurrent({ ...started, conversationId, stream });
    setMinimized(false);
    setPhase("connecting");
    setRelayActive(false);
    return started;
  }, [current]);

  const stopPresenting = useCallback(async () => {
    if (!current) return;
    const { conversationId, share_id, stream } = current;
    try {
      await endShare(conversationId, share_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not end share");
    }
    try { wsRef.current?.close(); } catch { /* noop */ }
    try { await sfuRef.current?.disconnect?.(); } catch { /* noop */ }
    wsRef.current = null;
    sfuRef.current = null;
    stream?.getTracks().forEach((t) => {
      try { t.stop(); } catch { /* noop */ }
    });
    setCurrent(null);
    setMinimized(false);
    setPhase("connecting");
    setRelayActive(false);
  }, [current]);

  // ── Wire up WS + SFU once ``current`` is set ─────────────────
  // iter135.18 — mic publish state for the presenter side. System
  // audio (from ``getDisplayMedia``) is published automatically by
  // ``presenterConnect`` — this separate handle is for publishing
  // the presenter's microphone on top, same as viewer-side mic.
  const [hasSystemAudio, setHasSystemAudio] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [micBusy, setMicBusy] = useState(false);
  const [activeSpeakers, setActiveSpeakers] = useState([]);
  const toggleMic = useCallback(async () => {
    const sfu = sfuRef.current;
    if (!sfu || micBusy) return;
    setMicBusy(true);
    try {
      if (micOn) {
        await sfu.unpublishMic();
        setMicOn(false);
        if (current?.share_id && current?.conversationId) {
          api.post(`/conv/conversations/${current.conversationId}/share/${current.share_id}/mic`,
                   { action: "mute" }, { silent: true }).catch(() => {});
        }
      } else {
        await sfu.publishMic();
        setMicOn(true);
        if (current?.share_id && current?.conversationId) {
          api.post(`/conv/conversations/${current.conversationId}/share/${current.share_id}/mic`,
                   { action: "publish" }, { silent: true }).catch(() => {});
        }
      }
    } catch (e) {
      toast.error(`${t("convv2.audio_mic_permission_denied")}: ${e?.message || ""}`);
    } finally {
      setMicBusy(false);
    }
  }, [micOn, micBusy, current, t]);

  useEffect(() => {
    if (!current?.token) return;
    const tracks = current.stream?.getTracks() || [];
    const onTrackEnded = () => stopPresenting();
    tracks.forEach((t) => t.addEventListener("ended", onTrackEnded));
    // iter136.11 — WS reconnect with backoff. Matches the backend
    // PRESENTER_RECONNECT_GRACE (10s) — we get 3 retry attempts
    // inside that window, plenty for Wi-Fi roam / brief stalls.
    let cancelled = false;
    let ws = null;
    let reconnectTimer = null;
    let attempts = 0;
    const openWithRetry = () => {
      if (cancelled) return;
      ws = openSignalingWs(current.token, {
        onJoined: async () => {
          attempts = 0;
          setPhase("active");
          // SFU connect only needs to run on the FIRST successful
          // attach; subsequent reconnects of the signaling WS
          // don't re-open LiveKit (which has its own reconnect).
          if (sfuRef.current) return;
          try {
            const sfu = await presenterConnect({
              shareAccessToken: current.token,
              mediaStream: current.stream,
              onConnected: () => setRelayActive(true),
              onDisconnected: () => setRelayActive(false),
              onActiveSpeakers: setActiveSpeakers,
            });
            sfuRef.current = sfu;
            setHasSystemAudio(!!sfu.hasSystemAudio);
          } catch (e) {
            if (e?.response?.status === 501) {
              setPhase("relay_unavailable");
            } else {
              toast.error(`SFU connect failed: ${e?.message || "unknown"}`);
            }
          }
        },
        onClose: (ev) => {
          // Intentional close (share stopped) — policy_violation
          // from server OR 1000 from our own cleanup. Don't retry.
          // 1008 = policy_violation (auth rejected), 1000 = normal.
          const code = ev?.code;
          const isTerminal = code === 1000 || code === 1008;
          if (cancelled || isTerminal || attempts >= 3) {
            setPhase("disconnected");
            return;
          }
          attempts += 1;
          // Small backoff so we don't thrash on a persistent network
          // outage: 300ms, 1s, 3s. Backend grace is 10s total.
          const delay = [300, 1000, 3000][attempts - 1] || 3000;
          setPhase("reconnecting");
          reconnectTimer = setTimeout(openWithRetry, delay);
        },
      });
      wsRef.current = ws;
    };
    openWithRetry();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      // Same StrictMode-safe cleanup as the in-page version: do
      // NOT stop tracks here — that lives in stopPresenting and
      // the browser's native stop-share chrome.
      tracks.forEach((t) => t.removeEventListener("ended", onTrackEnded));
      try { ws?.close(1000, "unmount"); } catch { /* noop */ }
      sfuRef.current?.disconnect?.();
      sfuRef.current = null;
      setHasSystemAudio(false);
      setMicOn(false);
      setActiveSpeakers([]);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.share_id]);

  // ── Best-effort cleanup on tab close / refresh ───────────────
  // The browser's beforeunload event lets us fire-and-forget the
  // ``end_share`` POST + the LiveKit disconnect. ``sendBeacon`` is
  // the only synchronous-friendly transport, but our endpoint
  // requires custom auth headers, so we use ``fetch`` with
  // ``keepalive: true`` instead. The OS already releases the
  // capture on tab close so the screen-share track stops naturally.
  useEffect(() => {
    if (!current) return;
    const handler = () => {
      try {
        const { conversationId, share_id } = current;
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/conv/conversations/${conversationId}/share/${share_id}/end`;
        // Use plain fetch with ``keepalive`` so the request survives
        // the page unloading. Auth headers are best-effort: we mirror
        // what the global axios interceptor would attach.
        const headers = {
          "Content-Type": "application/json",
        };
        try {
          const u = JSON.parse(localStorage.getItem("usair_user") || "{}");
          if (u?.email) headers["X-User-Email"] = u.email;
        } catch { /* noop */ }
        try {
          const cs = localStorage.getItem("usair_conv_session");
          if (cs) headers["Authorization"] = `Bearer ${cs}`;
        } catch { /* noop */ }
        fetch(url, { method: "POST", keepalive: true, headers })
          .catch(() => {});
      } catch { /* noop — tab is going down anyway */ }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [current]);

  const ctxValue = useMemo(() => ({
    startPresenting, stopPresenting, current,
    minimized, setMinimized, phase, relayActive,
    hasSystemAudio, micOn, micBusy, toggleMic, activeSpeakers,
  }), [startPresenting, stopPresenting, current, minimized, phase,
      relayActive, hasSystemAudio, micOn, micBusy, toggleMic,
      activeSpeakers]);

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      {current && (
        <PresenterOverlay
          current={current}
          phase={phase}
          relayActive={relayActive}
          minimized={minimized}
          hasSystemAudio={hasSystemAudio}
          micOn={micOn}
          micBusy={micBusy}
          toggleMic={toggleMic}
          onMinimize={() => setMinimized(true)}
          onExpand={() => setMinimized(false)}
          onStop={stopPresenting}
        />
      )}
    </Ctx.Provider>
  );
}

// ── Overlay: full-screen modal OR floating pill ─────────────────

function PresenterOverlay({ current, phase, relayActive, minimized,
                           hasSystemAudio, micOn, micBusy, toggleMic,
                           onMinimize, onExpand, onStop }) {
  const t = useT();
  const videoRef = useRef(null);
  // Always re-attach the stream when the video element mounts
  // (mounting/unmounting happens whenever the user toggles
  // minimize). The MediaStream itself never changes for a session.
  useEffect(() => {
    if (videoRef.current && current.stream) {
      videoRef.current.srcObject = current.stream;
      videoRef.current.play?.().catch(() => {});
    }
  }, [current.stream, minimized]);

  if (minimized) {
    return (
      <button
        type="button"
        onClick={onExpand}
        data-testid="conv-share-minimized-pill"
        className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 px-3 py-2 rounded-full shadow-lg hover:brightness-95"
        style={{ background: "#00a884", color: "white" }}
      >
        <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
        <Monitor size={14} weight="bold" />
        <span className="text-[12px] font-bold uppercase tracking-wider">
          {t("convv2.presenter_pill_label")}
        </span>
        {/* Compact mic pill — a separate button inside the pill.
            Click stops propagation so the outer button (which
            expands the modal) doesn't fire. */}
        <span
          onClick={(e) => { e.stopPropagation(); toggleMic(); }}
          role="button"
          aria-pressed={micOn}
          aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
          data-testid="conv-presenter-pill-mic-btn"
          title={micOn ? "Mute microphone" : "Unmute microphone"}
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
            micOn ? "" : "opacity-90"
          }`}
          style={{ background: micOn ? "white" : "rgba(255,255,255,0.25)", color: micOn ? "#00a884" : "white" }}
        >
          {micOn
            ? <Microphone size={12} weight="bold" />
            : <MicrophoneSlash size={12} weight="bold" />}
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); onStop(); }}
          role="button"
          aria-label="Stop sharing"
          data-testid="conv-share-stop-btn-pill"
          className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-white/20"
          style={{ background: "rgba(255,255,255,0.15)" }}
        >
          <XIcon size={12} weight="bold" />
        </span>
      </button>
    );
  }

  // iter135.9 — badge label prioritises the LiveKit relay state.
  // The gateway WS is just signaling — once LiveKit has the screen
  // track published, frames flow regardless of whether the
  // signaling WS got idle-timed-out by the proxy. Only show
  // "Connecting…" / "Disconnected" when the relay itself is down.
  const phaseLabel = relayActive
    ? "Live · Relaying"
    : phase === "active"
    ? "Live · Direct only"
    : phase === "relay_unavailable"
    ? "Live · Direct only"
    : phase === "connecting"
    ? "Connecting…"
    : "Disconnected";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(15, 23, 42, 0.85)" }}
      data-testid="conv-presenter-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-[90vw] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Monitor size={16} weight="duotone" style={{ color: "#00a884" }} />
            <span className="font-bold text-[14px] text-slate-800">
              {t("convv2.presenter_title")}
            </span>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                relayActive ? "text-white" : "text-slate-500 bg-slate-100"
              }`}
              style={relayActive ? { background: "#00a884" } : undefined}
              data-testid="conv-share-phase"
            >
              {phaseLabel}
            </span>
            {hasSystemAudio && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white flex items-center gap-1"
                style={{ background: "#3b82f6" }}
                data-testid="conv-presenter-sysaudio-pill"
                title={t("convv2.audio_sharing_tooltip")}
              >
                <SpeakerHigh size={10} weight="bold" />
                {t("convv2.audio_sharing_pill")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMic}
              disabled={micBusy}
              data-testid="conv-presenter-mic-btn"
              aria-pressed={micOn}
              title={micOn ? t("convv2.audio_mic_mute") : t("convv2.audio_mic_unmute")}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider disabled:opacity-60 flex items-center gap-1 ${
                micOn
                  ? "text-white"
                  : "border border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
              style={micOn ? { background: "#00a884" } : undefined}
            >
              {micOn
                ? <Microphone size={11} weight="bold" />
                : <MicrophoneSlash size={11} weight="bold" />}
              {micOn ? t("convv2.audio_mic_on") : t("convv2.audio_mic_off")}
            </button>
            <button
              onClick={onMinimize}
              data-testid="conv-share-minimize-btn"
              title={t("convv2.presenter_minimize_tip")}
              className="px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 text-[11px] font-bold uppercase tracking-wider hover:bg-slate-50 flex items-center gap-1"
            >
              <ArrowsIn size={11} weight="bold" />
              {t("convv2.presenter_minimize")}
            </button>
            <button
              onClick={onStop}
              data-testid="conv-share-stop-btn"
              className="px-3 py-1.5 rounded-full text-white text-[11px] font-bold uppercase tracking-wider"
              style={{ background: "#ef4444" }}
            >
              <XIcon size={11} weight="bold" className="inline mr-1" />
              {t("convv2.presenter_stop")}
            </button>
          </div>
        </div>
        <video
          ref={videoRef}
          muted
          playsInline
          className="w-full bg-slate-900"
          style={{ aspectRatio: "16 / 9", maxHeight: "70vh" }}
          data-testid="conv-share-preview"
        />
        <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
          {relayActive
            ? t("convv2.presenter_relay_frames")
            : phase === "relay_unavailable"
            ? t("convv2.presenter_relay_unavailable")
            : t("convv2.presenter_relay_connecting")}
        </div>
      </div>
    </div>
  );
}

// Keep an unused-import marker so eslint doesn't complain when the
// component isn't using the icon yet.
const _unused = ArrowsOut; void _unused;
